import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { Media } from '../models/media/media.model';
import { BaseBackendService } from './base-backend.service';
import { Observable, of } from 'rxjs';
import { SsoAuthService } from './sso.service';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root', // This makes the LeadService globally available throughout the app
})
export class MediaService extends BaseBackendService<Media> {
    constructor(
        http: HttpClient,
        protected override authService: AuthService,
        ssoService: SsoAuthService
    ) {
        super(http, authService, Media, 'media', ssoService);
    }

    uploadImageToS3(imageFile: File, filename: string,folderId: number = null): Observable<any> {
        if (!imageFile) {
            return of({ message: 'No file passed' });
        }

        const orgId = this.authService.getCurrentOrganization().id;
        const makeBodyToInitialize = this.createInitBody(orgId, filename, folderId);

        return this.initializeImageUploadToS3(makeBodyToInitialize).pipe(
            switchMap((mediaPostRes: any) =>
                this.uploadToS3(mediaPostRes, imageFile)
            ),
            switchMap((mediaPostRes: any) => {
                const response = this.updateMediaDetails(
                    mediaPostRes,
                    imageFile,
                    filename
                );
                return response;
            }),
            catchError((error) => {
                console.error('Error during S3 upload process:', error);
                return of({ message: 'Error in upload process', error });
            })
        );
    }

    // To initialize the image upload to S3
    private initializeImageUploadToS3(passedBody): Observable<any> {
        const PASSED_QUERY_PARAMS = `?content_type=image&organization=${passedBody.organization}`;
        return this.post(passedBody, PASSED_QUERY_PARAMS).pipe(
            map((response) => {
                return response;
            }),
            catchError((error) => {
                console.error('NOT ABLE TO INITIALIZE S3 UPLOAD');
                return this.handleError(error);
            })
        );
    }

    // To create body for media initialization
    private createInitBody(orgId: number, filename: string,folderId?: number): any {
        const body = {
            name: filename,
            organization: orgId,
            public: true,
        };
        if (folderId) {
            body['folder'] = folderId;
        }
        return body;
    }

    // To upload image to S3 then get details of s3 state from server
    private uploadToS3(mediaPostRes: any, imageFile: File): Observable<any> {
        const formData = this.createFormData(mediaPostRes, imageFile);
        return this.http.post(mediaPostRes['post_action_url'], formData).pipe(
            switchMap(() => {
                const getImageData = this.getDetailS3Details(mediaPostRes['id']);
                return getImageData;
            })
        );
    }

    // To create form data for image upload to S3
    private createFormData(mediaPostRes: any, imageFile: File): FormData {
        const formData = new FormData();
        formData.append('key', mediaPostRes['key']);
        formData.append('Policy', mediaPostRes['policy']);
        formData.append('X-Amz-Signature', mediaPostRes['x-amz-signature']);
        formData.append('X-Amz-Algorithm', mediaPostRes['x-amz-algorithm']);
        formData.append('X-Amz-Date', mediaPostRes['x-amz-date']);
        formData.append('X-Amz-Credential', mediaPostRes['x-amz-credential']);
        formData.append(
            'X-Amz-Security-Token',
            mediaPostRes['x-amz-security-token']
        );
        formData.append('Content-Type', imageFile.type);
        formData.append('file', imageFile);
        return formData;
    }

    // To get the details of media from server
    private getDetailS3Details(imageMediaId: string): Observable<any> {
        return this.getData(imageMediaId);
    }

    // To Update the state of media in server
    private updateMediaDetails(
        mediaRes: any,
        imageFile: File,
        fileName: string
    ): Observable<any> {
        mediaRes.name = fileName;
        mediaRes.status = 'Active';
        mediaRes.content_type = imageFile.type;
        return this.put(mediaRes, mediaRes.id, '', 1);
    }
}
